import type * as React from 'react'
import { useEffect, useRef } from 'react'
import type { JSX } from 'react'

import {
  Button,
  IconButton,
  CopyIcon,
  useControllableState,
  PencilIcon,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { NewPostComposerTrigger } from '~publish/components/NewPostComposerTrigger'
import { TemplateDetails } from '~publish/components/TemplateDetails'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { useIsViewOnlyPublishUser } from '~publish/hooks/useIsViewOnlyPublishUser'
import AppActionCreators from '~publish/legacy/composer/composer/action-creators/AppActionCreators'

import type { Template } from '../../types'

import {
  TemplatePreviewDialog,
  useCopyTemplateBody,
} from './TemplatePreviewDialog'

import styles from './TemplateDialog.module.css'

type TemplateDialogProps = {
  template: Template
  cta: CTA | null
  preselectChannelId?: string
  triggerId?: string
  /**
   * Custom (end-user-owned) templates skip the "By Buffer" attribution
   * and curated goal/type tags — those fields are server-defaulted for
   * non-manager actors and would mislabel the user's own content.
   */
  isCustom?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children?: React.ReactNode
  /** When true (with `onEdit`), an Edit action is shown next to the close button. */
  canEdit?: boolean
  onEdit?: () => void
  /** Overrides the default "By Buffer" attribution (e.g. for custom templates). */
  byline?: React.ReactNode
}

export const TemplateDialog = ({
  template,
  cta,
  preselectChannelId,
  triggerId,
  isCustom = false,
  open: openProp,
  onOpenChange,
  children,
  canEdit = false,
  onEdit,
  byline,
}: TemplateDialogProps): JSX.Element | null => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: false,
    onChange: onOpenChange,
  })

  const organizationId = useOrganizationId()
  const isViewOnlyPublishUser = useIsViewOnlyPublishUser()
  const omniboxIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const rafIdRef = useRef<number | null>(null)

  useEffect(() => {
    return (): void => {
      if (omniboxIntervalRef.current !== null) {
        clearInterval(omniboxIntervalRef.current)
        omniboxIntervalRef.current = null
      }
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
    }
  }, [])

  const copyTemplateBody = useCopyTemplateBody(template?.body)

  if (!template) {
    return null
  }

  const handleOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen) {
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null
        const templateTrigger = document.getElementById(
          triggerId ?? `template-card-${template.id}`,
        )
        if (templateTrigger) {
          templateTrigger.focus()
        }
      })
    }
    setOpen(nextOpen)
  }

  const handleCreatePost = (): void => {
    BufferTracker.templateUsed({
      cta: cta ?? 'create-templates-dialog-newPost-1',
      organizationId: organizationId ?? '',
      product: 'publish',
      templateId: template.id,
      templateUse: 'createPost',
    })

    if (omniboxIntervalRef.current !== null) {
      clearInterval(omniboxIntervalRef.current)
    }

    const start = Date.now()
    omniboxIntervalRef.current = setInterval(() => {
      AppActionCreators.updateOmniboxState(true)
      if (Date.now() - start > 2000 && omniboxIntervalRef.current !== null) {
        clearInterval(omniboxIntervalRef.current)
        omniboxIntervalRef.current = null
      }
    }, 200)

    handleOpenChange(false)
  }

  return (
    <TemplatePreviewDialog
      template={template}
      open={open}
      onOpenChange={handleOpenChange}
      trigger={children}
      actions={
        <>
          <Button variant="secondary" size="medium" onClick={copyTemplateBody}>
            <CopyIcon />
            Copy template
          </Button>
          {!isViewOnlyPublishUser && (
            <NewPostComposerTrigger
              channels={preselectChannelId ? [preselectChannelId] : undefined}
              prefillPostData={{ text: template.body }}
              cta="create-templates-dialog-newPost-1"
              templateId={template.id}
            >
              <Button
                variant="primary"
                size="medium"
                onClick={handleCreatePost}
              >
                Use Template
              </Button>
            </NewPostComposerTrigger>
          )}
        </>
      }
      metadata={
        byline || !isCustom ? (
          <TemplateDetails.Metadata byline={byline} showTags={!isCustom} />
        ) : null
      }
      headerAction={
        canEdit && onEdit ? (
          <IconButton
            variant="tertiary"
            size="small"
            label="Edit template"
            className={styles.editButton}
            onClick={onEdit}
          >
            <PencilIcon />
          </IconButton>
        ) : null
      }
    />
  )
}
